import {
  ArrowDownLeft,
  ArrowRight,
  Building2,
  CreditCard,
  Layers,
  Package,
  ShieldCheck,
  Tag,
} from 'lucide-react'
import { useAppStore } from '@/store/appStore'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import type { OrgProduct } from './types'

// ─── Product display config by asset name keyword ────────────────────────────

interface ProductConfig {
  icon: React.ReactNode
  bgColor: string
  textColor: string
  borderColor: string
}

function getProductConfig(name: string): ProductConfig {
  const n = name.toLowerCase()
  if (n.includes('recaudo'))
    return {
      icon: <ArrowDownLeft size={20} />,
      bgColor: 'bg-emerald-50',
      textColor: 'text-emerald-700',
      borderColor: 'border-emerald-200',
    }
  if (n.includes('payin') || n.includes('pago'))
    return {
      icon: <CreditCard size={20} />,
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-700',
      borderColor: 'border-blue-200',
    }
  if (n.includes('empresa') || n.includes('portal'))
    return {
      icon: <Building2 size={20} />,
      bgColor: 'bg-brand-50',
      textColor: 'text-brand-700',
      borderColor: 'border-brand-200',
    }
  if (n.includes('batch') || n.includes('lote'))
    return {
      icon: <Layers size={20} />,
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-700',
      borderColor: 'border-purple-200',
    }
  return {
    icon: <Package size={20} />,
    bgColor: 'bg-gray-50',
    textColor: 'text-gray-700',
    borderColor: 'border-gray-200',
  }
}

interface ProductoCardProps {
  product: OrgProduct
}

export function ProductoCard({ product }: ProductoCardProps) {
  const { asset, userRoles, isCurrentPortal, portal_url, offering_name, offering_id } = product
  const config = getProductConfig(asset.name)
  const { auth } = useAppStore()

  // Rol más privilegiado para mostrar (el que tiene ADMIN o el primero)
  const displayRole =
    userRoles.find((r) => r.endsWith('_ADMINISTRATOR') || r.endsWith('_ADMIN')) ??
    userRoles[0] ??
    '—'

  const isClickable = !isCurrentPortal && !!portal_url

  const href = isClickable && portal_url && auth.idToken
    ? `${portal_url}${auth.idToken}`
    : undefined

  const cardClasses = cn(
    'flex flex-col shadow-sm transition-all duration-200',
    isCurrentPortal
      ? 'border-brand-300 ring-1 ring-brand-200'
      : 'hover:shadow-md hover:-translate-y-0.5',
    isClickable && 'cursor-pointer',
  )

  const cardContent = (
    <Card className={cardClasses}>
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between gap-3">
          <div
            className={cn(
              'flex h-11 w-11 items-center justify-center rounded-xl border',
              config.bgColor,
              config.textColor,
              config.borderColor,
            )}
          >
            {config.icon}
          </div>
          <div className="flex flex-col items-end gap-1">
            {isCurrentPortal && (
              <Badge variant="outline" className="gap-1 border-brand-300 text-brand-600 text-[10px]">
                Este portal
              </Badge>
            )}
            {isClickable && (
              <Badge variant="outline" className="gap-1 text-[10px]">
                <ArrowRight size={9} />
                Acceder
              </Badge>
            )}
            <Badge variant="secondary" className="font-mono text-[10px]">
              {asset.type}
            </Badge>
          </div>
        </div>

        <div className="mt-3 space-y-1.5">
          <h3 className="font-display text-base font-bold text-text-primary leading-tight">{asset.name}</h3>
          {offering_name && (
            <div className="flex items-center gap-1.5">
              <Tag size={10} className="shrink-0 text-text-quaternary" />
              <span
                className="truncate text-[10px] font-medium uppercase tracking-wide text-text-tertiary"
                title={offering_id ?? undefined}
              >
                {offering_name}
              </span>
            </div>
          )}
          {asset.description && (
            <p className="text-xs text-text-tertiary line-clamp-2">{asset.description}</p>
          )}
        </div>
      </CardHeader>

      <CardContent className="mt-auto space-y-3 pt-0">
        <div className="h-px bg-border-secondary" />

        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5">
            <ShieldCheck size={13} className="text-text-quaternary shrink-0" />
            <span className="text-xs text-text-tertiary">Tu rol:</span>
          </div>
          <Badge variant="default" className="text-[10px] font-bold uppercase tracking-wide">
            {displayRole.replace(/_/g, ' ')}
          </Badge>
        </div>

        {userRoles.length > 1 && (
          <p className="text-[10px] text-text-quaternary text-right">
            +{userRoles.length - 1} rol{userRoles.length - 1 !== 1 ? 'es' : ''} adicional
            {userRoles.length - 1 !== 1 ? 'es' : ''}
          </p>
        )}
      </CardContent>
    </Card>
  )

  if (href) {
    return (
      <a href={href} className="block no-underline">
        {cardContent}
      </a>
    )
  }

  return cardContent
}
